import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminUsersApi, adminSubscriptionsApi } from '../api/adminApi';
import { UserWithProfile, LoadingStatus, VerificationStatus } from '../../types';

interface AdminUsersState {
  users: UserWithProfile[];
  total: number;
  page: number;
  limit: number;
  status: LoadingStatus;
  error: string | null;
}

const initialState: AdminUsersState = {
  users: [],
  total: 0,
  page: 1,
  limit: 20,
  status: 'idle',
  error: null,
};

export const fetchUsers = createAsyncThunk(
  'adminUsers/fetch',
  async (params: Record<string, unknown> = {}, { rejectWithValue }) => {
    try {
      const res = await adminUsersApi.list(params);
      return res.data;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to fetch users');
    }
  },
);

export const updateUserStatus = createAsyncThunk(
  'adminUsers/updateStatus',
  async ({ id, status, suspensionReason }: { id: string; status: string; suspensionReason?: string }, { rejectWithValue }) => {
    try {
      await adminUsersApi.updateStatus(id, status, suspensionReason);
      return { id, status, suspensionReason };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to update status');
    }
  },
);

export const deleteUser = createAsyncThunk(
  'adminUsers/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await adminUsersApi.delete(id);
      return id;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to delete user');
    }
  },
);

export const updateUserVerification = createAsyncThunk(
  'adminUsers/updateVerification',
  async ({ id, verificationStatus }: { id: string; verificationStatus: VerificationStatus }, { rejectWithValue }) => {
    try {
      await adminUsersApi.updateVerification(id, verificationStatus);
      return { id, verificationStatus };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to update verification status');
    }
  },
);

export const revokeUserSubscription = createAsyncThunk(
  'adminUsers/revokeSubscription',
  async (userId: string, { rejectWithValue }) => {
    try {
      await adminSubscriptionsApi.revoke(userId);
      return userId;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'No active subscription found');
    }
  },
);

const adminUsersSlice = createSlice({
  name: 'adminUsers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchUsers.fulfilled, (state, { payload }) => {
        state.users = payload.users;
        state.total = payload.total;
        state.page = payload.page;
        state.limit = payload.limit;
        state.status = 'succeeded';
      })
      .addCase(fetchUsers.rejected, (state, { payload }) => {
        state.status = 'failed';
        state.error = payload as string;
      })
      .addCase(updateUserStatus.fulfilled, (state, { payload }) => {
        const user = state.users.find((u) => u._id === payload.id);
        if (user?.profile) user.profile.status = payload.status as typeof user.profile.status;
      })
      .addCase(deleteUser.fulfilled, (state, { payload }) => {
        state.users = state.users.filter((u) => u._id !== payload);
        state.total -= 1;
      })
      .addCase(updateUserVerification.fulfilled, (state, { payload }) => {
        const user = state.users.find((u) => u._id === payload.id);
        if (user?.profile) user.profile.verificationStatus = payload.verificationStatus;
      });
  },
});

export default adminUsersSlice.reducer;
export const selectAdminUsers = (s: { adminUsers: AdminUsersState }) => s.adminUsers.users;
export const selectAdminUsersTotal = (s: { adminUsers: AdminUsersState }) => s.adminUsers.total;
export const selectAdminUsersStatus = (s: { adminUsers: AdminUsersState }) => s.adminUsers.status;
export const selectAdminUsersPage = (s: { adminUsers: AdminUsersState }) => s.adminUsers.page;
export const selectAdminUsersLimit = (s: { adminUsers: AdminUsersState }) => s.adminUsers.limit;
